import { inject, Injectable } from '@angular/core';
import { firstValueFrom, BehaviorSubject } from 'rxjs';
import { ConfigService } from '../../../services/config.service';
import {
  TaxOfficeClientService,
  TaxOfficeResponseDto,
  UpdateTaxOfficeRequestDto,
  ConnectLogoUploadRequestDto,
  FileUploadResponseDto,
  UserRole
} from '../../../core/api';
import { AuthService } from '../../../services/auth.service';
import { ILogger } from '../../../interfaces/logger.interface';
import { LoggerFactory } from '../../../utils/logger-factory';
import { UserService } from '../../../services/user.service';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private readonly _logger: ILogger = LoggerFactory.getLogger('SettingsService');
  private readonly _taxOfficeClientService = inject(TaxOfficeClientService);
  private readonly _authService = inject(AuthService);
  private readonly _userService = inject(UserService);

  // Track if initial load has been attempted
  private _initialLoadAttempted = false;

  private readonly _taxOfficeData = new BehaviorSubject<TaxOfficeResponseDto | null>(null);
  public readonly taxOfficeData$ = this._taxOfficeData.asObservable();

  /**
   * Gets the current tax office data for the authenticated user and updates the observable
   */
  public async getTaxOfficeData(): Promise<TaxOfficeResponseDto | null> {
    try {
      const taxOfficeId = this._authService.getTaxOfficeId();

      if (!taxOfficeId) {
        this._logger.writeWarn('No tax office ID found in token');
        return null;
      }

      const result = await firstValueFrom(this._taxOfficeClientService.getTaxOffice(taxOfficeId));

      this._taxOfficeData.next(result);
      return result;
    } catch (error) {
      this._logger.writeError('Failed to get tax office data', error);
      throw error;
    }
  }

  /**
   * Updates the tax office data. Only accessible for users with Owner role.
   */
  public async updateTaxOfficeData(updateData: UpdateTaxOfficeRequestDto): Promise<TaxOfficeResponseDto | null> {
    try {
      await this.validateOwnerPermissions();

      const taxOfficeId = this._authService.getTaxOfficeId();
      if (!taxOfficeId) {
        this._logger.writeWarn('No tax office ID found in token');
        return null;
      }

      const result = await firstValueFrom(this._taxOfficeClientService.updateTaxOffice(taxOfficeId, updateData));

      this._taxOfficeData.next(result);

      return result;
    } catch (error) {
      this._logger.writeError('Failed to update tax office data', error);
      throw error;
    } finally {
      // Refresh UserService data to update sidebar
      await this._userService.refreshUserData();
    }
  }

  /**
   * Uploads a logo file for the tax office using two-step process. Only accessible for users with Owner role.
   * Step 1: Upload file to get uploadId
   * Step 2: Connect the uploadId to the tax office
   */
  public async uploadTaxOfficeLogo(logoFile: File): Promise<TaxOfficeResponseDto | null> {
    try {
      await this.validateOwnerPermissions();

      // Step 1: Upload the logo file and get uploadId
      this._logger.writeInfo('Step 1: Uploading logo file...');
      const uploadResponse: FileUploadResponseDto = await firstValueFrom(this._taxOfficeClientService.uploadLogoFile(logoFile));

      if (!uploadResponse.uploadId) {
        this._logger.writeError('No uploadId received from file upload');
        throw new Error('File upload failed - no upload ID received');
      }

      this._logger.writeInfo(`Step 1 completed. Upload ID: ${uploadResponse.uploadId}`);

      // Step 2: Connect the uploaded logo to the tax office
      this._logger.writeInfo('Step 2: Connecting logo to tax office...');
      const connectRequest: ConnectLogoUploadRequestDto = {
        uploadId: uploadResponse.uploadId
      };

      const result: TaxOfficeResponseDto = await firstValueFrom(this._taxOfficeClientService.connectLogoUpload(connectRequest));

      this._taxOfficeData.next(result);

      this._logger.writeInfo('Step 2 completed. Logo successfully connected to tax office.');
      return result;
    } catch (error) {
      this._logger.writeError('Failed to upload tax office logo', error);
      throw error;
    } finally {
      // Refresh UserService data to update sidebar with new logo
      await this._userService.refreshTaxOfficeData();
    }
  }

  /**
   * Initializes the service by loading tax office data if not already loaded
   */
  public async initializeIfNeeded(): Promise<void> {
    if (!this._initialLoadAttempted) {
      this._initialLoadAttempted = true;
      try {
        await this.getTaxOfficeData();
        this._logger.writeInfo('Tax office data initialized successfully');
      } catch (error) {
        this._logger.writeError('Failed to initialize tax office data', error);
      }
    }
  }

  /**
   * Gets the full logo API URL for a tax office with cache-busting timestamp
   */
  public getLogoApiUrl(taxOfficeId?: number): string | null {
    if (!taxOfficeId) {
      return null;
    }
    const baseUrl = ConfigService.getConfig().api;
    return `${baseUrl}/api/v1/TaxOffice/${taxOfficeId}/Logo?t=${Date.now()}`;
  }

  /**
   * Gets the current tax office's logo API URL
   */
  public getCurrentTaxOfficeLogoUrl(): string | null {
    const currentTaxOfficeData = this._taxOfficeData.value;
    if (!currentTaxOfficeData) {
      return null;
    }
    return this.getLogoApiUrl(currentTaxOfficeData.id);
  }

  /**
   * Deletes the tax office logo. Only accessible for users with Owner role.
   */
  public async deleteTaxOfficeLogo(): Promise<TaxOfficeResponseDto | null> {
    try {
      await this.validateOwnerPermissions();
      const taxOfficeId = this._authService.getTaxOfficeId();

      if (!taxOfficeId) {
        this._logger.writeWarn('No tax office ID found in token');
        return null;
      }

      this._logger.writeInfo(`Deleting tax office logo for ID: ${taxOfficeId}`);
      const result = await firstValueFrom(this._taxOfficeClientService.deleteTaxOfficeLogo(taxOfficeId));
      this._taxOfficeData.next(result);
      this._logger.writeInfo('Tax office logo deleted successfully');
      return result;
    } catch (error) {
      this._logger.writeError('Failed to delete tax office logo', error);
      throw error;
    } finally {
      // Refresh UserService data to update sidebar with new logo
      await this._userService.refreshTaxOfficeData();
    }
  }

  /**
   * Removes all clients and related data for the current user's tax office. Owner-only, non-production only.
   */
  public async removeAllClientData(): Promise<void> {
    await this.validateOwnerPermissions();

    this._logger.writeInfo('Removing all client data for tax office...');
    await firstValueFrom(this._taxOfficeClientService.removeAllClientDataAsync());
    this._logger.writeInfo('All client data removed successfully');
  }

  private async validateOwnerPermissions(): Promise<void> {
    if (!this._userService.hasRole([UserRole.Owner])) {
      this._logger.writeWarn(`User does not have owner role`);
      throw new Error(`Insufficient permissions`);
    }
  }
}
